import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import {
  TOEstablishTheColorOfLog,
  hasExternalIp,
  renderTypeCell,
} from "../../../../../components/ImportantFunctions";
import LanguageIcon from "@mui/icons-material/Language";
import BackupIcon from "@mui/icons-material/Backup";
import { useNavigate } from "react-router-dom";
import { dataGridStyle } from "../../../../../theme";
import { redirectMap } from "../../../../../utils/utils";

const ALL_FIELDS = ["dayOfWeek", "hour", "ipSorgente", "ipDestinatario", "dettaglio"];

const FIELD_CONFIG = {
  dayOfWeek:      { label: "Day",         color: "#43B4B9" },
  hour:           { label: "Hour",        color: "#F57E21" },
  ipSorgente:     { label: "Source",      color: "#562984" },
  ipDestinatario: { label: "Dest", color: "#77D167" },
  dettaglio:      { label: "Detail",      color: "#596035" },
};

const MatchedLogChart = ({ matchedCombinations }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [highlightedFields, setHighlightedFields] = useState([]);
  const timezone = window.sessionStorage.getItem("timezone");
  const navigate = useNavigate();

  const handleLinkClick = (highlightDettaglio, tipo) => {
    window.sessionStorage.setItem("selectedTab", redirectMap[tipo].name);
    if (window.sessionStorage.getItem("type") === "reseller") {
      navigate(`/reseller/myC/${redirectMap[tipo].url}?exact_details=${encodeURIComponent(highlightDettaglio)}`);
    } else {
      window.location.href = `/user/security/${redirectMap[tipo].url}?exact_details=${encodeURIComponent(highlightDettaglio)}`;
    }
  };

  const handleRowClick = (item) => {
    setSelectedLogs(item.logs || []);
    setHighlightedFields(item.matched_fields || []);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setHighlightedFields([]);
    setSelectedLogs([]);
    setOpenDialog(false);
  };

  if (!matchedCombinations || matchedCombinations.length === 0) {
    return (
      <Typography sx={{ fontSize: 20, fontWeight: 600, m: 5 }}>
        No correlations available
      </Typography>
    );
  }

  const sorted = [...matchedCombinations].sort((a, b) => b.log_count - a.log_count);
  const maxCount = sorted[0]?.log_count || 1;

  return (
    <>
      <Box sx={{ width: "100%", userSelect: "none" }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            mb: 1,
            color: "#aaa",
          }}
        >
          Combinations Sorted by Count
        </Typography>

        {/* Column headers */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, px: 1 }}>
          <Box sx={{ display: "flex", gap: 0.5, minWidth: 310, flexShrink: 0 }}>
            {ALL_FIELDS.map((f) => (
              <Typography
                key={f}
                sx={{
                  fontSize: 10,
                  color: "#555",
                  fontWeight: 600,
                  width: 58,
                  textAlign: "center",
                }}
              >
                {FIELD_CONFIG[f].label}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ fontSize: 10, color: "#555", fontWeight: 600, ml: 2 }}>
            anomalies
          </Typography>
        </Box>

        {/* Data rows */}
        {sorted.map((item, index) => {
          const activeFields = new Set(item.matched_fields);
          const barPct = (item.log_count / maxCount) * 100;

          return (
            <Box
              key={index}
              onClick={() => handleRowClick(item)}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 0.4,
                px: 1,
                py: 0.3,
                cursor: "pointer",
                borderRadius: 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" },
              }}
            >
              {/* Field word pills */}
              <Box sx={{ display: "flex", gap: 0.5, minWidth: 310, flexShrink: 0 }}>
                {ALL_FIELDS.map((f) => {
                  const active = activeFields.has(f);
                  return (
                    <Box
                      key={f}
                      sx={{
                        width: 58,
                        textAlign: "center",
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        backgroundColor: active ? FIELD_CONFIG[f].color : "transparent",
                        border: active ? "none" : "1px solid #333",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: active ? 700 : 400,
                          color: active ? "#fff" : "#444",
                          lineHeight: 1.2,
                        }}
                      >
                        {FIELD_CONFIG[f].label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Bar */}
              <Box sx={{ flex: 1, mx: 1.5, height: 18 }}>
                <Box
                  sx={{
                    width: `${barPct}%`,
                    height: "100%",
                    backgroundColor: "#1565c0",
                    borderRadius: 0.5,
                    minWidth: 4,
                  }}
                />
              </Box>

              {/* Count */}
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#ddd",
                  minWidth: 28,
                  textAlign: "right",
                }}
              >
                {item.log_count}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Matched Logs
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div style={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={selectedLogs.map((log, index) => ({ id: index, ...log }))}
              columns={columns(highlightedFields, timezone, handleLinkClick)}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              sx={dataGridStyle}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MatchedLogChart;


const columns = (highlightedFields, timezone, handleLinkClick) => [
  {
    field: "timeStamp",
    headerName: `TIMESTAMP (${timezone ? timezone : "GMT"})`,
    minWidth: 180,
    headerAlign: "left",
    align: "left",
    cellClassName:
      highlightedFields.includes("hour") || highlightedFields.includes("dayOfWeek")
        ? "highlighted-column"
        : "",
  },
  {
    field: "ipSorgente",
    headerName: "SOURCE",
    minWidth: 180,
    headerAlign: "left",
    align: "left",
    filterable: false,
    cellClassName: highlightedFields.includes("ipSorgente") ? "highlighted-column" : "",
    renderCell: (params) => (
      <div className="clickable-cell">
        {params.row.hostNameAtt || params.value.toString()}
      </div>
    ),
  },
  {
    field: "ipDestinatario",
    headerName: "DESTINATION",
    minWidth: 180,
    headerAlign: "left",
    align: "left",
    filterable: false,
    cellClassName: highlightedFields.includes("ipDestinatario") ? "highlighted-column" : "",
    renderCell: (params) => (
      <div className="clickable-cell">
        {params.row.hostNameDest || params.value.toString()}
      </div>
    ),
  },
  {
    field: "tipo",
    headerName: "TYPE",
    headerAlign: "center",
    flex: 1,
    minWidth: 100,
    align: "center",
    renderCell: renderTypeCell,
  },
  {
    field: "dettaglio",
    headerName: "DETAILS",
    flex: 1,
    minWidth: 280,
    headerAlign: "left",
    align: "left",
    filterable: false,
    cellClassName: highlightedFields.includes("dettaglio") ? "highlighted-column" : "",
    renderCell: (params) => (
      <div
        className="detail-clickable-cell"
        style={{ color: TOEstablishTheColorOfLog(params.value) }}
      >
        {params.value}
        {hasExternalIp(params.row.ipDestinatario, params.row.ipSorgente) && (
          <Tooltip
            title={
              <Typography sx={{ fontSize: 15, color: "orange" }}>
                External Connection
              </Typography>
            }
            arrow
          >
            <LanguageIcon />
          </Tooltip>
        )}
        {params.row.backup && (
          <Tooltip
            title={
              <Typography sx={{ fontSize: 15, color: "#61a9ad" }}>
                Possible Backup
              </Typography>
            }
            arrow
          >
            <BackupIcon />
          </Tooltip>
        )}
      </div>
    ),
  },
  {
    field: "main-table",
    headerName: "MAIN TABLE",
    headerAlign: "center",
    flex: 1,
    minWidth: 100,
    align: "center",
    renderCell: (params) => (
      <Button
        sx={{
          backgroundColor: "#2d2d2d !important",
          color: "#eaeaea !important",
          fontWeight: 900,
          "&:hover": {
            backgroundColor: "#eaeaea !important",
            color: "#131C2D !important",
          },
        }}
        onClick={() => handleLinkClick(params.row.dettaglio, params.row.tipo)}
      >
        Main Table
      </Button>
    ),
  },
];
